import type { AppLocale } from "../i18n/format";

/**
 * The amount keypad.
 *
 * WHY A CUSTOM PAD AND NOT THE OS KEYBOARD.
 * The OS numeric keyboard is a general-purpose surface: it covers the content,
 * its keys are small, and on Android it varies by vendor. This pad PUSHES the
 * rows up rather than covering them — the whole point of a converter is
 * watching the other currencies change as you type, and a keyboard that hides
 * them defeats it. It also lets one press mean "clear everything", which no OS
 * keyboard can offer.
 *
 * ARITHMETIC IS DELIBERATELY ABSENT — BUT IT IS SCHEDULED.
 * The reference pad carries AC ÷ × − +, and the real case is common: standing
 * in a shop adding three items and shipping. It is omitted here because dead
 * keys are worse than absent ones, and evaluating an expression properly is
 * its own feature, scheduled AFTER the drag and the sheets. When it is built:
 *
 *   - a real tokenizer and a shunting-yard parser, not string splitting
 *   - decimal.js at every step, never a JS float
 *   - NEVER eval() and NEVER new Function(). This field accepts pasted text,
 *     so an expression evaluator here is an untrusted-input parser and must be
 *     written as one.
 *
 * Latin digits only, in both locales, per the digit invariant.
 */

interface Props {
  readonly locale: AppLocale;
  readonly onDigit: (digit: string) => void;
  readonly onDot: () => void;
  readonly onBackspace: () => void;
  readonly onClear: () => void;
  readonly onDone: () => void;
  /** True while a clear can be undone, so the key can say so. */
  readonly canRestore: boolean;
}

function BackspaceGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M9 5h11a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9L2.5 12z" />
      <path d="M17 9.5 12 14.5M12 9.5l5 5" />
    </svg>
  );
}

function RestoreGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 9h11a5 5 0 1 1 0 10H8" />
      <path d="M6.5 5.5 3 9l3.5 3.5" />
    </svg>
  );
}

export function Keypad({
  locale,
  onDigit,
  onDot,
  onBackspace,
  onClear,
  onDone,
  canRestore,
}: Props) {
  const ar = locale === "ar";

  return (
    <div className="keypad" role="group" aria-label={ar ? "لوحة الأرقام" : "Number pad"}>
      {/*
       * SOURCE ORDER IS THE LAYOUT. The grid flows in four columns, so the
       * children are emitted row by row — digits then the utility key that
       * ends that row. Emitting all ten digits first and the utility keys
       * afterwards produced "7 8 9 4 / 5 6 1 2", which is what happens when
       * source order and visual order are allowed to drift apart.
       */}
      <div className="keypad__grid">
        {["7", "8", "9"].map((d) => (
          <button key={d} type="button" className="key key--digit" onClick={() => onDigit(d)}>
            {d}
          </button>
        ))}

        <button
          type="button"
          className="key key--action"
          onClick={onBackspace}
          /*
           * The label changes when a clear can be undone, so the second press
           * is announced honestly rather than being a hidden mode. Sighted
           * users discover it by instinct; screen reader users are told.
           */
          aria-label={
            canRestore
              ? ar
                ? "استعادة المبلغ الممسوح"
                : "Restore the cleared amount"
              : ar
                ? "مسح المبلغ"
                : "Clear amount"
          }
        >
          {canRestore ? <RestoreGlyph /> : <BackspaceGlyph />}
        </button>

        {["4", "5", "6"].map((d) => (
          <button key={d} type="button" className="key key--digit" onClick={() => onDigit(d)}>
            {d}
          </button>
        ))}

        <button
          type="button"
          className="key key--action"
          onClick={onClear}
          aria-label={ar ? "مسح الكل" : "Clear all"}
        >
          AC
        </button>

        {["1", "2", "3"].map((d) => (
          <button key={d} type="button" className="key key--digit" onClick={() => onDigit(d)}>
            {d}
          </button>
        ))}

        <button type="button" className="key key--done" onClick={onDone}>
          {ar ? "تم" : "Done"}
        </button>

        <button type="button" className="key key--digit key--wide" onClick={() => onDigit("0")}>
          0
        </button>
        <button
          type="button"
          className="key key--digit"
          onClick={onDot}
          aria-label={ar ? "فاصلة عشرية" : "Decimal point"}
        >
          .
        </button>
      </div>
    </div>
  );
}
